import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography, Container, Divider, Paper, Link, Stack } from '@mui/material';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';

import { images } from '../../../../../shared/lib/assets';
import { useContactOptMutation, useValidateTokenOptInLazyQuery } from '../../../generated';
import { Alert } from '../../../../../shared/lib/common/Alert';
import { useParams } from 'react-router-dom';
import ConfirmationDialog from '../../../components/common/CustomConfirmationDialog';
import { contactOptInButtonStyle, inviteInfoStyle } from '../../../../../shared/lib/theme/styleConstants';
import ContactFeature from '../../../components/common/ContactFeature';
import { BRAND_NAME_DOT_COM, COMPANY_NAME_RIGHTS, LABEL_SECURE_PONELESS_SERVICES } from '../../../../../shared/lib/constants';
import { withTrademark } from '../../../../../shared/lib/common/Tokenizer';
import { MSG_INVALID_OR_EXPIRED_LINK } from '../../constants';

const OptInContact = () => {
  const { id } = useParams();

  const [open, setOpen] = useState(false);
  const [optedIn, setOptedIn] = useState(false);
  const [optedOut, setOptedOut] = useState(false);
  const [error, setError] = useState(false);

  const [userInfo, setUserInfo] = useState<{
    name: string;
    phoneNo: string;
  } | null>(null);

  const [contactOpt, { loading: contactOptLoading }] = useContactOptMutation({
    onCompleted: (data) => {
      const { contactOpt } = data ?? {};
      if (contactOpt) {
        Alert.success(contactOpt);
        setOpen(false);
      }
    },
    onError: (error) => {
      Alert.error(error.message);
    }
  });

  const [validateToken, { loading: validateTokenLoading }] = useValidateTokenOptInLazyQuery({
    onCompleted: (data) => {
      const { name, phoneNo } = data?.validateTokenOptIn || {};
      if (!name && !phoneNo) {
        setError(true);
        return;
      }
      setUserInfo({ name, phoneNo });
    },
    onError: () => {
      Alert.error(MSG_INVALID_OR_EXPIRED_LINK);
      setError(true);
    }
  });

  const onClick = async () => {
    if (id) {
      await contactOpt({
        variables: {
          input: { optIn: optedIn, token: id }
        }
      });
    }
  };

  const validateContact = useCallback(() => {
    if (id) {
      validateToken({
        variables: { optOutContactInput: { token: id } }
      });
    }
  }, [id, validateToken]);

  useEffect(() => {
    validateContact();
  }, [validateContact]);

  const loading = validateTokenLoading || contactOptLoading;

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      {/* Logo */}
      <Box display="flex" justifyContent="center" mb={4}>
        <Link href="https://855whodoicall.com" target="_blank" underline="none">
          <Box component="img" src={images.DASHBOARD_LOGO as unknown as string} alt="855WhoDoICall.com" sx={{ height: 60 }} />
        </Link>
      </Box>
      {/* Loader */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Paper
          elevation={4}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 5,
            background: 'linear-gradient(180deg, #F9FAFB 0%, #FFFFFF 100%)'
          }}
        >
          {/* Error State */}
          {error ? (
            <Typography variant="h5" color="error" align="center" fontWeight={600}>
              Invalid or Expired Link
            </Typography>
          ) : (
            <>
              {/* Header */}
              <Typography variant="h3" fontWeight={800} align="center" gutterBottom>
                Trusted Contact Confirmation
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" align="center" mb={4}>
                {LABEL_SECURE_PONELESS_SERVICES} <strong>{COMPANY_NAME_RIGHTS}</strong>
              </Typography>

              {/* Inviter Info */}
              <Box sx={inviteInfoStyle}>
                <Typography variant="subtitle2" color="text.secondary">
                  Invitation from
                </Typography>

                <Typography variant="h6" fontWeight={600}>
                  {userInfo?.name}
                </Typography>

                <Link href={`tel:${userInfo?.phoneNo}`} sx={{ fontSize: 16, textDecoration: 'none' }}>
                  {userInfo?.phoneNo}
                </Link>
              </Box>

              {/* Description */}
              <Typography variant="body1" mb={3} align="center">
                <strong>{userInfo?.name}</strong> has selected you as a <strong>VIP Trusted Contact</strong> on {withTrademark(BRAND_NAME_DOT_COM)}. If they ever lose access to their phone, our system allows them to
                reach you securely from any phone.
              </Typography>

              {/* Features */}
              <Stack spacing={2} mb={4}>
                <ContactFeature icon={<SmsOutlinedIcon />} text="You’ll receive a text alert before the call" />
                <ContactFeature icon={<PhoneInTalkOutlinedIcon />} text="A toll-free call will be placed on their behalf" />
                <ContactFeature icon={<CheckCircleOutlineIcon />} text={`Caller ID will display ${userInfo?.name}’s name`} />
              </Stack>

              {/* Verification */}
              <Typography variant="body2" align="center" mb={4}>
                You can verify this service by calling <Link href={`tel:${userInfo?.phoneNo}`}>{userInfo?.name}</Link> directly.
              </Typography>

              <Divider sx={{ my: 4 }} />

              {/* CTA */}
              {!optedIn && !optedOut ? (
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight={700} gutterBottom>
                    Confirm Your Preference
                  </Typography>

                  <Typography variant="body1" mb={3}>
                    Choose whether you’d like to participate as a Trusted Contact for <strong>{userInfo?.name}</strong>.
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => {
                        setOpen(true);
                        setOptedIn(true);
                      }}
                      sx={contactOptInButtonStyle}
                    >
                      Opt In
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      size="large"
                      onClick={() => {
                        setOpen(true);
                        setOptedOut(true);
                      }}
                      sx={contactOptInButtonStyle}
                    >
                      Reject
                    </Button>
                  </Stack>
                </Box>
              ) : (
                !open && (
                  <>
                    {optedIn ? (
                      <Box textAlign="center">
                        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
                        <Typography variant="h5" fontWeight={700} color="success.main">
                          You’re now confirmed!
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          Thank you for being a Trusted Contact.
                        </Typography>
                      </Box>
                    ) : (
                      <Box textAlign="center">
                        <Typography variant="h5" fontWeight={700} color="error.main" gutterBottom>
                          You’ve Declined
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          You will not receive calls or text notifications related to <strong>{userInfo?.name}</strong>.
                        </Typography>
                      </Box>
                    )}
                  </>
                )
              )}
            </>
          )}
        </Paper>
      )}
      {open && (
        <ConfirmationDialog
          open={open}
          onClose={() => {
            setOpen(false);
            setOptedIn(false);
            setOptedOut(false);
          }}
          loading={loading}
          onConfirm={onClick}
          message={optedOut ? 'Are you sure you want to reject?' : 'Are you sure you want to opt in?'}
        />
      )}
    </Container>
  );
};

export default OptInContact;
